/* eslint-disable implicit-arrow-linebreak */
import { useMemo } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { upperFirst } from 'lodash';
import {
  searchableIndexesValues,
  searchableIndexesMap,
} from '../../../constants/searchableIndexes';

// Authority API utilities for searching and fetching authority records
// Based on actual FOLIO authority APIs from stripes-authority-components

// API endpoints from FOLIO
const AUTHORITIES_SEARCH_API = 'search/authorities';
const AUTHORITIES_BROWSE_API = 'browse/authorities';

// Debounce utility for search requests
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Format headings query مطابق لنمط FOLIO stripes-authority-components
const formatHeadingsQuery = (indexData, queryTemplate, seeAlsoJoin = 'or') => {
  const queryStrings = indexData.map((data) => {
    const queryParts = [];

    if (data.plain) {
      queryParts.push(queryTemplate(data.name));
    }

    if ((data.sft || data.saft) && data.plain) {
      const name = upperFirst(data.name);

      if (data.sft) {
        queryParts.push(queryTemplate(`sft${name}`));
      }

      if (data.saft) {
        queryParts.push(queryTemplate(`saft${name}`));
      }
    }

    if (data.sft && !data.plain) {
      queryParts.push(queryTemplate(data.name));
    }

    if (data.saft && !data.plain) {
      queryParts.push(queryTemplate(data.name));
    }

    return queryParts;
  });

  const joinedQueryParts = queryStrings.flat().join(` ${seeAlsoJoin} `);
  return `(${joinedQueryParts})`;
};

export const buildQuery = ({
  searchIndex,
  query,
  sourceFileId = null,
  comparator = '==',
  seeAlsoJoin = 'or',
}) => {
  const indexData = searchableIndexesMap[searchIndex];

  if (!indexData) {
    return '';
  }

  const escapedQuery = query.replace(/"/g, '\\"');
  let baseQuery;

  if (searchIndex === searchableIndexesValues.KEYWORD) {
    baseQuery = `(${searchableIndexesValues.KEYWORD}=="${escapedQuery}" or naturalId=="${escapedQuery}")`;
  } else {
    const queryTemplate = (name) => `${name}${comparator}"${escapedQuery}"`;
    baseQuery = formatHeadingsQuery(indexData, queryTemplate, seeAlsoJoin);
  }

  // إضافة source filter إذا كان محدد
  if (sourceFileId && sourceFileId !== '') {
    return `${baseQuery} and (sourceFileId=="${sourceFileId}")`;
  }

  return baseQuery;
};

// Map direct search results to match Elasticsearch structure
const mapDirectSearchResult = (authority) => {
  if (!authority) return null;

  // Find the heading field from various authority types
  const headingRef =
    authority.personalName ||
    authority.topicalTerm ||
    authority.corporateName ||
    authority.meetingName ||
    authority.uniformTitle ||
    authority.geographicName ||
    authority.genreTerm ||
    authority.heading;

  return {
    ...authority,
    headingRef, // Add unified heading field
    heading: headingRef, // For backward compatibility
  };
};

export const buildAuthorityQuery = (query, searchType, sourceFileId = null) => {
  const searchIndexMap = {
    personalName: searchableIndexesValues.PERSONAL_NAME,
    corporateName: searchableIndexesValues.CORPORATE_NAME,
    meetingName: searchableIndexesValues.MEETING_NAME,
    uniformTitle: searchableIndexesValues.UNIFORM_TITLE,
    subject: searchableIndexesValues.SUBJECT,
    all: searchableIndexesValues.KEYWORD,
  };

  const searchIndex =
    searchIndexMap[searchType] || searchableIndexesValues.KEYWORD;

  return buildQuery({
    searchIndex,
    query,
    sourceFileId,
  });
};

// Search authority records directly from authority-storage endpoint
export const searchAuthorityDirect = async (
  query,
  searchType = 'searchType', // Same as searchAuthorityRecords
  limit = 10,
  ky,
) => {
  if (!query || query.trim().length < 3) {
    return { authorities: [], totalRecords: 0 };
  }

  try {
    // Build CQL query for direct authority-storage search using database fields
    let cqlQuery;

    if (searchType === 'cql') {
      cqlQuery = query;
    } else {
      // Use database field 'heading' directly instead of Elasticsearch fields
      cqlQuery = `heading=="${query.trim()}"`;
    }

    const searchParams = {
      query: cqlQuery,
      limit,
    };

    const response = await ky
      .get('authority-storage/authorities', { searchParams })
      .json();

    // Map results to match Elasticsearch structure
    const mappedAuthorities = (response.authorities || [])
      .map(mapDirectSearchResult)
      .filter(Boolean);

    return {
      authorities: mappedAuthorities,
      totalRecords: response.totalRecords || 0,
    };
  } catch (error) {
    console.error('❌ [searchAuthorityDirect] Error:', error);
    throw new Error(
      `Failed to search authority records directly: ${error.message}`,
    );
  }
};

// Search for exact match using direct CQL query
export const searchExactMatch = async (
  query,
  searchType = 'searchType',
  limit = 5,
  ky,
) => {
  if (!query || query.trim().length < 3) {
    return { authorities: [], totalRecords: 0 };
  }

  try {
    // Build exact match CQL query using database field 'heading'
    const exactCqlQuery = `heading=="${query.trim()}"`;

    // Use direct database search instead of Elasticsearch
    const results = await searchAuthorityDirect(
      exactCqlQuery,
      'cql',
      limit,
      ky,
    );

    return results;
  } catch (error) {
    console.error('❌ [searchExactMatch] Error:', error);
    return { authorities: [], totalRecords: 0 };
  }
};

// Search authority records with fallback to direct endpoint
export const searchAuthorityWithFallback = async (
  query,
  searchType = 'searchType', // Same as searchAuthorityRecords
  limit = 10,
  ky,
) => {
  if (!query || query.trim().length < 3) {
    return { authorities: [], totalRecords: 0 };
  }

  try {
    const elasticResults = await searchAuthorityRecords(
      query,
      searchType,
      limit,
      ky,
    );

    // If found results, return them immediately
    if (elasticResults.authorities && elasticResults.authorities.length > 0) {
      return elasticResults;
    }

    const directResults = await searchAuthorityDirect(
      query,
      searchType,
      limit,
      ky,
    );

    return directResults;
  } catch (elasticError) {
    try {
      const directResults = await searchAuthorityDirect(
        query,
        searchType,
        limit,
        ky,
      );
      return directResults;
    } catch (directError) {
      console.error('Both search methods failed:', {
        elasticError,
        directError,
      });
      return { authorities: [], totalRecords: 0 };
    }
  }
};

// Search authority records using FOLIO's search API
export const searchAuthorityRecords = async (
  query,
  queryType = 'searchType', // 'searchType' or 'cql'
  limit = 10,
  ky,
) => {
  if (!query || (queryType === 'searchType' && query.trim().length < 3)) {
    return { authorities: [], totalRecords: 0 };
  }

  try {
    let cqlQuery;

    // إذا كان queryType = 'cql'، فالـ query هو CQL جاهز
    if (queryType === 'cql') {
      cqlQuery = query;
    } else {
      // للتوافق مع الكود القديم
      cqlQuery = buildAuthorityQuery(query.trim(), queryType);
    }

    const searchParams = {
      query: cqlQuery,
      limit,
    };

    const response = await ky
      .get(AUTHORITIES_SEARCH_API, { searchParams })
      .json();

    return {
      authorities: response.authorities || [],
      totalRecords: response.totalRecords || 0,
    };
  } catch (error) {
    console.error('Error searching authority records:', error);
    throw new Error(`Failed to search authority records: ${error.message}`);
  }
};

// Get authority record by ID
export const getAuthorityRecord = async (authorityId, ky) => {
  if (!authorityId) return null;

  try {
    const searchParams = {
      query: `(id==${authorityId})`,
      limit: 1,
    };

    const response = await ky
      .get(AUTHORITIES_SEARCH_API, { searchParams })
      .json();

    return response.authorities?.[0] || null;
  } catch (error) {
    console.error('Error fetching authority record:', error);
    throw new Error(`Failed to fetch authority record: ${error.message}`);
  }
};

// Format authority record for display
export const formatAuthorityForDisplay = (authorityRecord) => {
  if (!authorityRecord) return null;

  // Try different possible source field names from API
  const sourceFileId =
    authorityRecord.sourceFileId ||
    authorityRecord.source ||
    authorityRecord.sourceName ||
    authorityRecord.authSourceFileId ||
    authorityRecord.metadata?.source ||
    null;

  return {
    // Primary fields for linking (used by handleLinkAuthority)
    id: authorityRecord.id,
    headingRef: authorityRecord.headingRef,
    // Additional fields for logging and compatibility
    authorityId: authorityRecord.id,
    heading: authorityRecord.headingRef,
    text: authorityRecord.headingRef,
    naturalId: authorityRecord.naturalId,
    headingType: authorityRecord.headingType,
    authRefType: authorityRecord.authRefType,
    displayText: authorityRecord.headingRef || authorityRecord.naturalId,
    sourceFileId,
    // Keep original record for debugging
    _original: authorityRecord,
  };
};

// Create authority record using FOLIO's records-editor API
export const createAuthorityRecord = async (recordData, ky) => {
  if (!recordData) {
    throw new Error('Record data is required');
  }

  try {
    const response = await ky
      .post('records-editor/records', { json: recordData })
      .json();

    return response;
  } catch (error) {
    console.error('Error creating authority record:', error);
    throw new Error(`Failed to create authority record: ${error.message}`);
  }
};

// Hook wrapper for using authority APIs with Okapi
export const useAuthorityApi = (tenantId) => {
  const ky = useOkapiKy({ tenant: tenantId });

  return useMemo(
    () => ({
      searchAuthorityRecords: (query, searchType, limit) =>
        searchAuthorityRecords(query, searchType, limit, ky),
      searchAuthorityDirect: (query, searchType, limit) =>
        searchAuthorityDirect(query, searchType, limit, ky),
      searchAuthorityWithFallback: (query, searchType, limit) =>
        searchAuthorityWithFallback(query, searchType, limit, ky),
      searchExactMatch: (query, searchType, limit) =>
        searchExactMatch(query, searchType, limit, ky),
      getAuthorityRecord: (authorityId) => getAuthorityRecord(authorityId, ky),
      createAuthorityRecord: (recordData) =>
        createAuthorityRecord(recordData, ky),
    }),
    [ky],
  );
};
